import azureDevOpsService from './azureDevOpsService';

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 heure

function buildCacheKey(squadName, runOrBuildId) {
  return `ai-suite-analysis:${encodeURIComponent(squadName)}:${runOrBuildId || 'unknown'}`;
}

/**
 * Lit le cache localStorage pour une suite donnée.
 * Retourne { result, savedAt } si trouvé et frais, null sinon.
 */
export function getCachedSuiteAnalysis(squadName, runOrBuildId) {
  try {
    const raw = localStorage.getItem(buildCacheKey(squadName, runOrBuildId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.result || !parsed?.savedAt) return null;
    if (Date.now() - new Date(parsed.savedAt).getTime() > CACHE_TTL_MS) {
      localStorage.removeItem(buildCacheKey(squadName, runOrBuildId));
      return null;
    }
    return { result: parsed.result, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

/**
 * Écrit le résultat d'analyse en cache localStorage.
 */
export function writeSuiteAnalysisCache(squadName, runOrBuildId, result) {
  try {
    localStorage.setItem(
      buildCacheKey(squadName, runOrBuildId),
      JSON.stringify({ result, savedAt: new Date().toISOString() })
    );
  } catch {}
}

/**
 * Envoie les tests échoués d'une suite au backend pour analyse IA globale.
 * @param {string} squadName
 * @param {Array<{id, name, error, status}>} testCases
 * @param {number} totalTests
 * @returns {Promise<{executiveSummary, clusters}>}
 */
export async function analyzeSuite(squadName, testCases, totalTests) {
  const failedTests = testCases
    .filter(t => t.status === 'failed')
    .map(t => ({
      id: t.id || '',
      name: t.name || '',
      error: t.stepError || t.error || '',
      logRunId: t.logRunId || null,
      logTestId: t.logTestId || null,
    }));

  if (failedTests.length === 0) throw new Error('Aucun test échoué à analyser');

  const pat = azureDevOpsService.getPat();
  const resp = await fetch('/api/ai/analyze-suite', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(pat ? { 'X-Azure-Devops-Pat': pat } : {})
    },
    body: JSON.stringify({ squadName, failedTests, totalTests: totalTests || testCases.length }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Erreur serveur (${resp.status})`);
  }

  const data = await resp.json();
  return data.analysis;
}
